import { cn } from "../lib/utils";
import type { TimelineEvent } from "../queries/useCustomer360";
import { Badge } from "./ui/badge";

type TimelineStyle = { dot: string; line: string; label: string };

const fallbackStyle: TimelineStyle = { dot: "bg-slate-400", line: "border-slate-100", label: "Update" };

const typeStyles: Partial<Record<TimelineEvent["type"], TimelineStyle>> = {
  quote_created: { dot: "bg-blue-600", line: "border-blue-100", label: "Quote" },
  message_sent: { dot: "bg-slate-500", line: "border-slate-100", label: "Touch" },
  reply_received: { dot: "bg-emerald-600", line: "border-emerald-100", label: "Reply" },
  draft_proposed: { dot: "bg-amber-600", line: "border-amber-100", label: "Draft" },
  conversation_note: { dot: "bg-violet-600", line: "border-violet-100", label: "Handled" },
  review_invite_sent: { dot: "bg-indigo-600", line: "border-indigo-100", label: "Review" },
  review_completed: { dot: "bg-emerald-600", line: "border-emerald-100", label: "Review" },
  opt_out: { dot: "bg-slate-900", line: "border-slate-200", label: "Compliance" },
  staff_reply_sent: { dot: "bg-violet-600", line: "border-violet-100", label: "Staff reply" },
  voice_followup: { dot: "bg-cyan-700", line: "border-cyan-100", label: "AI call" },
  vonigo_status_change: { dot: "bg-cyan-700", line: "border-cyan-100", label: "Vonigo" },
};

export function timelineStyle(event: TimelineEvent): TimelineStyle {
  if (event.type === "conversation_note") {
    const state = String(event.payload?.disposition_state || "").toUpperCase();
    const legacyDisposition = String(event.payload?.disposition || "").toLowerCase();
    if (state === "KEEP_FOLLOWUP") {
      return { dot: "bg-blue-600", line: "border-blue-100", label: "Keep follow-up" };
    }
    if (state === "CLOSED" || state === "CONVERTED" || legacyDisposition === "closed") {
      return { dot: "bg-red-600", line: "border-red-100", label: "Closed" };
    }
  }
  return typeStyles[event.type] || fallbackStyle;
}

function formatDate(value: string) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(date);
}

export function timelineEventText(event: TimelineEvent) {
  const payload = event.payload;
  if (!payload) return "";
  if (event.type === "conversation_note") {
    return typeof payload.note === "string" ? payload.note : "";
  }
  if (event.type === "voice_followup") {
    const outcomes: Record<string, string> = {
      needs_human: "Manager review needed.",
      do_not_call: "Future Murray outreach stopped.",
      no_answer: "No conversation confirmed. No automatic retry.",
      continue_followup: "Continue the existing follow-up cadence after the contact gap.",
    };
    return outcomes[String(payload.outcome || "")] || "";
  }
  const candidate = payload.body || payload.body_plain || payload.body_excerpt || payload.excerpt
    || payload.draft_body || payload.summary || payload.reason || payload.error;
  return typeof candidate === "string" ? candidate : "";
}

export function conversationNoteMeta(event: TimelineEvent) {
  if (event.type !== "conversation_note" || !event.payload) return [];
  const rawActor = String(event.payload.created_by || "").trim();
  const actor = rawActor.replace(/^\w/, (letter) => letter.toUpperCase());
  const reasonCode = String(event.payload.reason_code || "").trim();
  const reason = reasonCode
    ? reasonCode.replace(/_/g, " ").replace(/^\w/, (letter) => letter.toUpperCase())
    : "";
  return [actor ? `by ${actor}` : "", reason].filter(Boolean);
}

function payloadSubject(payload?: Record<string, unknown>) {
  if (!payload) return "";
  const candidate = payload.subject;
  return typeof candidate === "string" ? candidate : "";
}

function replyVariant(event: TimelineEvent) {
  const category = String(event.payload?.category || "");
  if (category === "ACCEPTED") return "green";
  if (category === "REFUS") return "red";
  if (category === "QUESTION" || category === "INDECIS") return "amber";
  return "slate";
}

function isCustomerMessageEvent(event: TimelineEvent) {
  return event.type === "reply_received" || event.payload?.direction === "inbound";
}

function channelLabel(event: TimelineEvent) {
  const channel = String(event.payload?.channel || "");
  const direction = String(event.payload?.direction || "");
  const isPreview = event.payload?.is_preview === true || String(event.payload?.status || "").toLowerCase() === "preview";
  if (channel === "sms" && direction === "inbound") return "Customer SMS";
  if (channel === "sms" && direction === "outbound") return isPreview ? "SMS draft - not sent" : "SMS sent";
  if (channel === "email" && direction === "inbound") return "Customer email";
  if (channel === "email" && direction === "outbound") return isPreview ? "Email draft - not sent" : "Email sent";
  return "";
}

function displayTitle(event: TimelineEvent, metaLabel: string) {
  if (event.label === metaLabel) return event.label;
  return event.label;
}

function showMetaLabel(event: TimelineEvent, metaLabel: string) {
  return Boolean(metaLabel && metaLabel !== event.label);
}

function missingBodyHint(event: TimelineEvent, subject: string) {
  if (event.type !== "message_sent") return "";
  if (subject) return "";
  const channel = String(event.payload?.channel || "");
  const isPreview = event.payload?.is_preview === true || String(event.payload?.status || "").toLowerCase() === "preview";
  if (isPreview) return "Draft body was not captured.";
  if (channel === "email") return "Older email sent. Body was not captured.";
  if (channel === "sms") return "Older SMS sent. Body was not captured.";
  return "";
}

export function Timeline({ events }: { events: TimelineEvent[] }) {
  if (events.length === 0) {
    return (
      <div className="rounded-md border border-dashed border-slate-200 bg-slate-50 px-4 py-8 text-center text-sm text-slate-500">
        No timeline events yet.
      </div>
    );
  }

  return (
    <div className="space-y-0">
      {events.map((event, index) => (
        <TimelineRow key={`${event.type}-${event.ts}-${event.quote_id || "none"}-${index}`} event={event} />
      ))}
    </div>
  );
}

function TimelineRow({ event }: { event: TimelineEvent }) {
  const isPreview = event.payload?.is_preview === true || String(event.payload?.status || "").toLowerCase() === "preview";
  const styles = isPreview
    ? { dot: "bg-amber-600", line: "border-amber-100", label: "Draft" }
    : timelineStyle(event);
  const text = timelineEventText(event);
  const subject = payloadSubject(event.payload);
  const metaLabel = channelLabel(event);
  const conversationMeta = conversationNoteMeta(event);
  const title = displayTitle(event, metaLabel);
  const customerMessage = isCustomerMessageEvent(event);
  const hint = !text ? missingBodyHint(event, subject) : "";

  return (
    <div className="grid grid-cols-[96px_20px_1fr] gap-3">
      <div className="pt-3 text-right text-xs text-slate-500">{formatDate(event.ts)}</div>
      <div className={cn("relative border-l", styles.line)}>
        <span className={cn("absolute -left-[5px] top-4 h-2.5 w-2.5 rounded-full", styles.dot)} />
      </div>
      <div className="border-b border-slate-100 py-3 text-left">
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant={event.type === "reply_received" ? replyVariant(event) : "slate"}>
            {styles.label}
          </Badge>
          {isPreview ? <Badge variant="amber">draft - not sent</Badge> : null}
          {event.quote_id ? <span className="text-xs font-medium text-slate-500">#{event.quote_id}</span> : null}
          <span className="text-sm font-medium text-slate-950">{title}</span>
          {showMetaLabel(event, metaLabel) ? <span className="text-xs text-slate-400">{metaLabel}</span> : null}
          {conversationMeta.map((item) => (
            <span key={item} className="text-xs text-slate-400">{item}</span>
          ))}
        </div>
        {subject ? (
          <div className="mt-2 text-xs font-semibold text-slate-700">Subject: {subject}</div>
        ) : null}
        {text ? (
          <p
            className={cn(
              "mt-2 whitespace-pre-wrap rounded-md border px-3 py-2 text-xs leading-relaxed",
              customerMessage
                ? "border-emerald-100 bg-emerald-50 text-slate-700 italic"
                : "border-slate-100 bg-slate-50 text-slate-700",
            )}
          >
            {text}
          </p>
        ) : null}
        {hint ? (
          <p className="mt-1 text-xs text-slate-400">{hint}</p>
        ) : null}
      </div>
    </div>
  );
}
